/**
 * A project's page (`/projects/:projectId`), home of its Agent runs panel
 * (#169 slice 3). Everything about the project is read live from GitLab as
 * the viewer, so the page loads on open, on window focus, after a change
 * and from Refresh, never on a timer (ruling 9).
 *
 * - `access: "connect"`: no usable credential; the connect prompt only.
 * - `read`: the panel with the reason it is read-only.
 * - The setup status (`SetupStatus`) heads the list: the harness assigned
 *   and CI wired turn the project's runs on. Until both are, `+ New`, Edit
 *   and Save are disabled with the reason (`setupBlockReason`).
 * - A 404 is "not found" whether the project is unknown or GitLab hides it,
 *   also on a refetch of a panel already shown (the viewer lost access).
 * - Any other failed refetch (a GitLab blip on window focus or Refresh)
 *   keeps the panel already shown, pause banner included, with an inline
 *   "Couldn't refresh" alert; the full-page error is for when there is no
 *   panel to show. The backend still enforces every write.
 *
 * The selected definition is `?def=<file_name>`, so a link is shareable;
 * other query params (the detail's `?tab=`) are kept when it changes. The
 * page owns the right pane's state (`useProjectPane`): the open editor
 * (`+ New` with write access, or Edit), closed by a selection, and a
 * withdraw's outcome or refusal, kept until the next selection or confirm.
 * An editor with unsaved changes is closed only after an inline "Discard
 * your changes?" (`DiscardPrompt`). Saving a new definition selects it
 * (`<name>.yaml`) while its editor is still open. The viewer's own
 * selection or editor action moves focus to the pane's heading, which also
 * scrolls it into view on the stacked phone layout.
 */

import type { ReactElement, ReactNode } from 'react';
import { useParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import {
  ConnectGitPrompt,
  DefinitionList,
  DefinitionPane,
  DiscardPrompt,
  PauseControl,
  SetupStatus,
  isNotFoundError,
  panelErrorMessage,
  projectPanelKeys,
  setupBlockReason,
  useProjectPane,
  useProjectPanel,
  type PanelResponse,
} from '@features/agent_runs';

function PageShell(props: {
  title: ReactNode;
  onRefresh?: () => void;
  refreshing?: boolean;
  children: ReactNode;
}): ReactElement {
  return (
    <div className="project-page">
      <header className="project-page__header">
        <h2 className="project-page__title">{props.title}</h2>
        {props.onRefresh && (
          <button
            type="button"
            className="secondary-button"
            onClick={props.onRefresh}
            disabled={props.refreshing === true}
          >
            {props.refreshing === true ? 'Refreshing…' : 'Refresh'}
          </button>
        )}
      </header>
      {props.children}
    </div>
  );
}

function projectTitle(panel: PanelResponse): ReactNode {
  if (panel.project_path === null) return 'Project';
  if (panel.web_url === null) return panel.project_path;
  return (
    <a href={panel.web_url} className="runs-page__link" target="_blank" rel="noreferrer">
      {panel.project_path}
    </a>
  );
}

function Panel({ projectId, panel }: { projectId: string; panel: PanelResponse }): ReactElement {
  const pane = useProjectPane();
  const blocked = setupBlockReason(panel.setup);
  return (
    <>
      {panel.read_only_reason !== null && (
        <p className="project-page__read-only">Read-only: {panel.read_only_reason}</p>
      )}
      <PauseControl projectId={projectId} panel={panel} />
      {pane.discard !== null && <DiscardPrompt discard={pane.discard} />}
      <div className="project-page__layout">
        <aside className="project-page__list" aria-label="Run definitions">
          <SetupStatus projectId={projectId} panel={panel} />
          {panel.access === 'write' && (
            <div className="project-page__new">
              <button
                type="button"
                className="secondary-button"
                disabled={blocked !== null}
                {...(blocked !== null ? { 'aria-describedby': 'project-new-blocked' } : {})}
                onClick={() => { pane.open({ kind: 'new' }); }}
              >
                + New
              </button>
              {blocked !== null && <span id="project-new-blocked" className="def-detail__hint">{blocked}</span>}
            </div>
          )}
          {panel.list_error !== null && (
            <div className="error-message" role="alert">{panel.list_error}</div>
          )}
          <DefinitionList
            definitions={panel.definitions}
            proposed={panel.proposed}
            truncated={panel.proposed_truncated}
            unchecked={panel.unchecked_mr_iids}
            defaultBranch={panel.default_branch}
            selected={pane.selected}
            onSelect={pane.select}
          />
        </aside>
        <section className="project-page__detail" aria-label="Selected definition">
          <DefinitionPane
            projectId={projectId}
            panel={panel}
            selected={pane.selected}
            editing={pane.editing}
            withdrawn={pane.withdrawn}
            focusPane={pane.focusPane}
            onEdit={(start) => { pane.open({ kind: 'edit', start }); }}
            onCloseEditor={pane.close}
            onSaved={pane.saved}
            onWithdrawn={pane.setWithdrawn}
            onDirtyChange={pane.setDirty}
          />
        </section>
      </div>
    </>
  );
}

export function ProjectPage(): ReactElement {
  const { projectId = '' } = useParams<{ projectId: string }>();
  const client = useQueryClient();
  const { data: panel, error, isLoading, isFetching, refetch } = useProjectPanel(projectId);

  const refresh = (): void => {
    void refetch();
    void client.invalidateQueries({ queryKey: projectPanelKeys.definitions(projectId) });
    void client.invalidateQueries({ queryKey: projectPanelKeys.allRuns(projectId) });
  };

  if (isLoading) {
    return (
      <PageShell title="Project">
        <div className="loading-message">Loading the project…</div>
      </PageShell>
    );
  }
  if (panel === undefined || isNotFoundError(error)) {
    // The same copy for an unknown project and one GitLab hides from the
    // viewer: the backend makes them indistinguishable on purpose.
    const message = isNotFoundError(error) ? 'Project not found.' : panelErrorMessage(error);
    return (
      <PageShell title="Project" onRefresh={refresh} refreshing={isFetching}>
        <div className="error-message" role="alert">{message}</div>
      </PageShell>
    );
  }
  if (panel.access === 'connect') {
    return (
      <PageShell title="Project">
        <ConnectGitPrompt />
      </PageShell>
    );
  }
  return (
    <PageShell title={projectTitle(panel)} onRefresh={refresh} refreshing={isFetching}>
      {error !== null && (
        <div className="error-message" role="alert">
          Couldn&apos;t refresh: {panelErrorMessage(error)}
        </div>
      )}
      <Panel projectId={projectId} panel={panel} />
    </PageShell>
  );
}
